import React, { useState } from "react";
import Icon from "../Icon";
import { formatFriendly } from "../helpers";
import { FormattedUpload } from "../types";
import ReverseConfirmationModal from "./ReverseConfirmationModal";

interface Props {
  record: FormattedUpload;
  canEdit: boolean;
  canReverse: boolean;
  onReverseRequest: (rec: FormattedUpload, reason: string) => void;
}

const ReverseControl: React.FC<Props> = ({
  record,
  canEdit,
  canReverse,
  onReverseRequest,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  if (record.status === "reversing") {
    return (
      <div
        className="sched-card"
        style={{ background: "var(--warn-bg)", borderColor: "var(--warn)" }}
      >
        <div className="sched-card-head">
          <div className="sched-card-icon" style={{ color: "var(--warn)" }}>
            <Icon name="refresh" size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sched-card-title" style={{ color: "var(--warn)" }}>
              Reversal in progress
            </div>
            <div className="sched-card-sub">
              We&rsquo;re marking the recon entries from this upload as
              reversed. Once that finishes, the file&rsquo;s products and value
              dates will be eligible for re-upload.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (record.status === "reversed") {
    return (
      <div className="sched-card" style={{ background: "var(--surface-2)" }}>
        <div className="sched-card-head">
          <div className="sched-card-icon" style={{ color: "var(--muted)" }}>
            <Icon name="check-circle" size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sched-card-title" style={{ color: "var(--ink-2)" }}>
              Reversed
              {record.reverseRequestedBy
                ? ` by ${record.reverseRequestedBy.name}`
                : ""}
              {record.reversedAt
                ? ` on ${formatFriendly(record.reversedAt, true)}`
                : ""}
            </div>
            <div className="sched-card-sub">
              The products and value dates from this upload are eligible for
              re-upload.
              {record.reverseReason && (
                <>
                  {" "}
                  Reason: <em>{record.reverseReason}</em>.
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const requested = !!record.reverseRequestedAt;

  if (requested && record.status === "processing") {
    return (
      <div
        className="sched-card"
        style={{ background: "var(--warn-bg)", borderColor: "var(--warn)" }}
      >
        <div className="sched-card-head">
          <div className="sched-card-icon" style={{ color: "var(--warn)" }}>
            <Icon name="clock" size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sched-card-title" style={{ color: "var(--warn)" }}>
              Reversal queued
            </div>
            <div className="sched-card-sub">
              Reconciliation is still running. The job will finish cleanly, then
              this upload will reverse.
              {record.reverseRequestedBy && (
                <>
                  {" "}
                  Requested by <strong>{record.reverseRequestedBy.name}</strong>
                  .
                </>
              )}
              {record.reverseReason && (
                <>
                  {" "}
                  Reason: <em>{record.reverseReason}</em>.
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canEdit) return null;

  return (
    <>
      <div className="sched-card">
        <div className="sched-card-head">
          <div
            className="sched-card-icon"
            style={{ background: "var(--danger-bg)", color: "var(--danger)" }}
          >
            <Icon name="refresh" size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="sched-card-title">Need to redo this period?</div>
            <div className="sched-card-sub">
              Reversing this upload marks its reconciliation entries reversed
              and frees the products + value dates for re-upload. The audit
              trail is preserved.
            </div>
          </div>
        </div>
        <div className="sched-actions">
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-danger-ghost"
            disabled={!canReverse}
            title={
              canReverse
                ? ""
                : "Only senior analysts can reverse a reconciliation"
            }
            onClick={() => setModalOpen(true)}
          >
            <Icon name="refresh" size={13} /> Reverse this reconciliation
          </button>
        </div>
        {!canReverse && (
          <div className="restricted-note">
            <Icon name="shield" size={12} /> Only senior analysts can reverse a
            completed reconciliation. Editors can still upload new files for
            value dates that aren&rsquo;t already reconciled.
          </div>
        )}
      </div>
      {modalOpen && (
        <ReverseConfirmationModal
          record={record}
          onConfirm={(reason) => {
            setModalOpen(false);
            onReverseRequest(record, reason);
          }}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
};

export default ReverseControl;
